import { z } from "zod";

import { UUID } from "../generic/uuid.ts";

// ================================================
// ENUMS
// ================================================

// RequestMessageTypeEnum
export const RequestMessageTypeEnum = z.enum(["request"]);
export type RequestMessageTypeEnum = z.infer<typeof RequestMessageTypeEnum>;

// ResponseMessageTypeEnum
export const ResponseMessageTypeEnum = z.enum(["response"]);
export type ResponseMessageTypeEnum = z.infer<typeof ResponseMessageTypeEnum>;

// MessageTypeEnum
export const MessageTypeEnum = z.union([
  RequestMessageTypeEnum,
  ResponseMessageTypeEnum,
]);
export type MessageTypeEnum = z.infer<typeof MessageTypeEnum>;

// RequestMessageAuthorEnum
export const RequestMessageAuthorEnum = z.enum(["human"]);
export type RequestMessageAuthorEnum = z.infer<typeof RequestMessageAuthorEnum>;

// ResponseMessageAuthorEnum
export const ResponseMessageAuthorEnum = z.enum(["ai", "system"]);
export type ResponseMessageAuthorEnum = z.infer<
  typeof ResponseMessageAuthorEnum
>;

// MessageAuthorEnum
export const MessageAuthorEnum = z.union([
  RequestMessageAuthorEnum,
  ResponseMessageAuthorEnum,
]);
export type MessageAuthorEnum = z.infer<typeof MessageAuthorEnum>;

// RequestMessageStatusEnum
export const RequestMessageStatusEnum = z.enum(["success"]);
export type RequestMessageStatusEnum = z.infer<typeof RequestMessageStatusEnum>;

// ResponseMessageStatusEnum
export const ResponseMessageStatusEnum = z.enum([
  "success",
  "info",
  "abort",
  "error",
]);
export type ResponseMessageStatusEnum = z.infer<
  typeof ResponseMessageStatusEnum
>;

// MessageStatusEnum
export const MessageStatusEnum = z.union([
  RequestMessageStatusEnum,
  ResponseMessageStatusEnum,
]);
export type MessageStatusEnum = z.infer<typeof MessageStatusEnum>;

// RequestMessageContentTypeEnum
export const RequestMessageContentTypeEnum = z.enum([
  "text", // plain text chat message
]);
export type RequestMessageContentTypeEnum = z.infer<
  typeof RequestMessageContentTypeEnum
>;

// ResponseMessageContentTypeEnum
export const ResponseMessageContentTypeEnum = z.enum([
  "text", // plain text chat message
  "summary", // summary of what was generated
  "context_summary", // LLM-optimized summary for context retrieval
  "suggestions", // suggested follow-up messages
  "followup", // post-summary AI nudge toward a next design step
]);
export type ResponseMessageContentTypeEnum = z.infer<
  typeof ResponseMessageContentTypeEnum
>;

// MessageContentTypeEnum
export const MessageContentTypeEnum = z.union([
  RequestMessageContentTypeEnum,
  ResponseMessageContentTypeEnum,
]);
export type MessageContentTypeEnum = z.infer<typeof MessageContentTypeEnum>;

// ================================================
// SELECT
// ================================================

// MessageBase
const MessageBase = z
  .strictObject({
    id: UUID,
    chat_id: UUID,
    sequence: z.int().min(1),
    content: z.string(),
    metadata: z.record(z.string(), z.any()),
    operation: z.nullable(z.string().trim().min(1)),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .refine((data) => data.created_at <= data.updated_at, {
    message: '"created_at" must be before or equal to "updated_at"',
  });

// RequestMessage
export const RequestMessage = MessageBase.safeExtend({
  request_message_id: z.null(),
  type: RequestMessageTypeEnum,
  author: RequestMessageAuthorEnum,
  status: RequestMessageStatusEnum,
  content_type: RequestMessageContentTypeEnum,
  command: z.nullable(z.string().trim().min(1)),
});
export type RequestMessage = z.infer<typeof RequestMessage>;

// ResponseMessage
export const ResponseMessage = MessageBase.safeExtend({
  request_message_id: UUID,
  type: ResponseMessageTypeEnum,
  author: ResponseMessageAuthorEnum,
  status: ResponseMessageStatusEnum,
  content_type: ResponseMessageContentTypeEnum,
  command: z.null(),
});
export type ResponseMessage = z.infer<typeof ResponseMessage>;

// Message
export const Message = z.discriminatedUnion("type", [
  RequestMessage,
  ResponseMessage,
]);
export type Message = z.infer<typeof Message>;

// ================================================
// INSERT
// ================================================

const omitInsertFields = {
  sequence: true, // auto-assigned by a trigger function
  created_at: true,
  updated_at: true,
} as const;

// RequestMessageInsert
export const RequestMessageInsert = RequestMessage.omit(
  omitInsertFields,
).partial({ id: true });
export type RequestMessageInsert = z.infer<typeof RequestMessageInsert>;

// ResponseMessageInsert
export const ResponseMessageInsert = ResponseMessage.omit(
  omitInsertFields,
).partial({ id: true });
export type ResponseMessageInsert = z.infer<typeof ResponseMessageInsert>;

// MessageInsert
export const MessageInsert = z.discriminatedUnion("type", [
  RequestMessageInsert,
  ResponseMessageInsert,
]);
export type MessageInsert = z.infer<typeof MessageInsert>;
